import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface MenuItem {
  label: string;
  path: string;
  icon: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    path: '/staredge_admin/dashboard',
    icon: 'ri-dashboard-line',
  },
  {
    label: 'User Management',
    path: '/staredge_admin/users',
    icon: 'ri-user-line',
    children: [
      {
        label: 'Overview',
        path: '/staredge_admin/users',
        icon: 'ri-user-line',
      },
      {
        label: 'AI Drama Creators',
        path: '/staredge_admin/users/ai-drama-creators',
        icon: 'ri-star-line',
      },
      {
        label: 'Creator Collection',
        path: '/staredge_admin/users/creator-collection',
        icon: 'ri-team-line',
      },
      {
        label: 'Bulk Creator Generation',
        path: '/staredge_admin/users/bulk-creator',
        icon: 'ri-user-search-line',
      },
    ],
  },
  {
    label: 'Content Moderation',
    path: '/staredge_admin/content',
    icon: 'ri-shield-check-line',
    children: [
      {
        label: 'Overview',
        path: '/staredge_admin/content',
        icon: 'ri-dashboard-line',
      },
      {
        label: 'Forum',
        path: '/staredge_admin/content/queue',
        icon: 'ri-inbox-line',
      },
      {
        label: 'Dramas',
        path: '/staredge_admin/content/dramas',
        icon: 'ri-movie-2-line',
      },
      {
        label: 'Bulk Dramas Create',
        path: '/staredge_admin/content/bulk-dramas',
        icon: 'ri-clapperboard-line',
      },
      {
        label: 'Dramas Review & Comment',
        path: '/staredge_admin/content/reviews',
        icon: 'ri-chat-check-line',
      },
      {
        label: 'Groups',
        path: '/staredge_admin/content/groups',
        icon: 'ri-group-line',
      },
      {
        label: 'Group Discussion',
        path: '/staredge_admin/content/discussions',
        icon: 'ri-discuss-line',
      },
      {
        label: 'Reports',
        path: '/staredge_admin/content/reports',
        icon: 'ri-flag-line',
      },
      {
        label: 'Events',
        path: '/staredge_admin/content/events',
        icon: 'ri-calendar-event-line',
      },
      {
        label: 'Audit Log',
        path: '/staredge_admin/content/audit-log',
        icon: 'ri-file-list-line',
      },
      {
        label: 'News Archive',
        path: '/staredge_admin/content/news',
        icon: 'ri-newspaper-line',
      },
      {
        label: 'Bulk News Create',
        path: '/staredge_admin/content/bulk-news',
        icon: 'ri-radar-line',
      },
    ],
  },
  {
    label: 'Settings',
    path: '/staredge_admin/settings',
    icon: 'ri-settings-3-line',
  },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const location = useLocation();
  const { logout } = useAdminAuth();

  // Auto-expand menu based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const parentMenu = menuItems.find(
      (item) =>
        item.children &&
        item.children.some((child) => currentPath.startsWith(child.path))
    );
    if (parentMenu && !expandedMenus.includes(parentMenu.label)) {
      setExpandedMenus([...expandedMenus, parentMenu.label]);
    }
  }, [location.pathname]);

  const toggleMenu = (label: string) => {
    if (sidebarCollapsed) return;
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isActive = (path: string, hasChildren?: boolean) => {
    if (hasChildren) {
      return location.pathname.startsWith(path);
    }
    return location.pathname === path;
  };

  const getPageTitle = () => {
    const currentPath = location.pathname;
    
    for (const item of menuItems) {
      if (item.children) {
        const child = item.children.find((c) => c.path === currentPath);
        if (child) {
          return `${item.label} / ${child.label}`;
        }
      }
    }
    
    const item = menuItems.find((item) => item.path === currentPath);
    return item?.label || 'Admin Panel';
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`bg-gray-900 text-white flex flex-col flex-shrink-0 h-screen transition-all duration-300 ease-in-out relative z-50 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="h-16 flex items-center justify-center border-b border-gray-800 flex-shrink-0">
            {sidebarCollapsed ? (
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-admin-line text-white text-lg" />
              </div>
            ) : (
              <h1 className="text-xl font-bold text-teal-400 whitespace-nowrap">Admin Panel</h1>
            )}
          </div>

          {/* Navigation - independently scrollable */}
          <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
            {menuItems.map((item) => (
              <div key={item.label} className="relative">
                {item.children ? (
                  <>
                    {/* Parent menu item */}
                    <button
                      onClick={() => toggleMenu(item.label)}
                      onMouseEnter={() => setHoveredItem(item.label)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-0' : 'justify-between px-6'} py-3 transition-colors whitespace-nowrap cursor-pointer ${
                        isActive(item.path, true)
                          ? 'bg-teal-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-3'}`}>
                        <i className={`${item.icon} text-lg ${sidebarCollapsed ? '' : ''}`}></i>
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </div>
                      {!sidebarCollapsed && (
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            expandedMenus.includes(item.label) ? 'rotate-180' : ''
                          }`}
                        ></i>
                      )}
                    </button>

                    {/* Tooltip for collapsed mode */}
                    {sidebarCollapsed && hoveredItem === item.label && (
                      <div className="absolute left-full top-0 ml-2 z-[100] bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-2 min-w-[200px]">
                        <div className="px-4 py-2 border-b border-gray-700">
                          <span className="text-sm font-semibold text-teal-400">{item.label}</span>
                        </div>
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-3 px-4 py-2 transition-colors whitespace-nowrap ${
                              isActive(child.path)
                                ? 'bg-teal-700 text-white'
                                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                            }`}
                          >
                            <i className={`${child.icon} text-sm`}></i>
                            <span className="text-sm">{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Expanded children (full sidebar mode) */}
                    {!sidebarCollapsed && expandedMenus.includes(item.label) && (
                      <div className="bg-gray-800">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-3 px-6 py-2.5 pl-14 transition-colors whitespace-nowrap ${
                              isActive(child.path)
                                ? 'bg-teal-700 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            <i className={`${child.icon} text-base`}></i>
                            <span className="text-sm">{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="relative">
                    <Link
                      to={item.path}
                      onMouseEnter={() => sidebarCollapsed ? setHoveredItem(item.label) : undefined}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`flex items-center ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-6'} py-3 transition-colors whitespace-nowrap ${
                        isActive(item.path)
                          ? 'bg-teal-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </Link>

                    {/* Tooltip for collapsed single items */}
                    {sidebarCollapsed && hoveredItem === item.label && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[100] bg-gray-900 border border-gray-700 rounded-md shadow-xl px-3 py-1.5">
                        <span className="text-sm text-white whitespace-nowrap">{item.label}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content - independently scrollable */}
      <div className="flex-1 flex flex-col h-screen min-w-0">
        {/* Top Bar - fixed */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 relative z-40 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-600"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <i className={`${sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl`}></i>
            </button>
            <h2 className="text-xl font-semibold text-gray-800">{getPageTitle()}</h2>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line"></i>
            <span>Logout</span>
          </button>
        </header>

        {/* Page Content - scrollable */}
        <main className="flex-1 p-8 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
